import type { PaginateResponse } from '@/utils/paginate'

import { toast } from 'sonner'
import { fDateTime } from '@/utils/format-date'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import axiosInstance, { endpoints } from '@/lib/axios'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Loader2,
  MessageSquare,
  Pencil,
  Plus,
  RefreshCw,
  Save,
  Trash2,
  X,
} from 'lucide-react'

import type { ClipComment } from '../clip.types'

const PAGE_LIMIT = 10

type Props = {
  clipId: string
}

function getDisplayName(comment: ClipComment): string {
  const user = comment.user
  if (!user) return '-'
  if (user.role === 'ADMIN') return 'RFC Club'
  if (user.firstName && user.lastName)
    return `${user.firstName} ${user.lastName}`
  return user.displayName || user.email || '-'
}

function getAvatar(comment: ClipComment): string | undefined {
  if (comment.user?.role === 'ADMIN') return '/images/rfc-logo.png'
  return comment.user?.imageUrl || comment.user?.photoURL
}

export default function ClipCommentsManager({ clipId }: Props) {
  const [comments, setComments] = useState<ClipComment[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [newContent, setNewContent] = useState('')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editingContent, setEditingContent] = useState('')

  const hasMore = useMemo(
    () => page < Math.ceil(total / PAGE_LIMIT),
    [page, total],
  )

  const fetchComments = useCallback(
    async (pageNumber = 1, append = false, showLoading = true) => {
      if (showLoading) {
        if (append) setIsLoadingMore(true)
        else setIsLoading(true)
      }

      try {
        const res = await axiosInstance.get<PaginateResponse<ClipComment>>(
          endpoints.clips.comments.list(clipId),
          {
            params: {
              page: pageNumber,
              limit: PAGE_LIMIT,
              orderBy: 'createdAt:desc',
            },
          },
        )
        const items = res.data.items ?? []
        setComments((prev) => (append ? [...prev, ...items] : items))
        setTotal(res.data.total)
        setPage(pageNumber)
      } catch (error) {
        console.error('fetch clip comments error', error)
        toast.error('โหลดความคิดเห็นล้มเหลว')
      } finally {
        if (showLoading) {
          setIsLoading(false)
          setIsLoadingMore(false)
        }
      }
    },
    [clipId],
  )

  useEffect(() => {
    fetchComments()
  }, [fetchComments])

  const handleCreate = async () => {
    const content = newContent.trim()
    if (!content) return

    setIsSubmitting(true)
    try {
      await axiosInstance.post(endpoints.clips.comments.create(clipId), {
        content,
      })
      setNewContent('')
      await fetchComments(1, false, false)
      toast.success('เพิ่มความคิดเห็นแล้ว')
    } catch (error) {
      console.error('create clip comment error', error)
      toast.error('เพิ่มความคิดเห็นล้มเหลว')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleEdit = (comment: ClipComment) => {
    setEditingId(comment.id)
    setEditingContent(comment.content)
  }

  const handleUpdate = async (commentId: string) => {
    const content = editingContent.trim()
    if (!content) return

    setIsSubmitting(true)
    try {
      await axiosInstance.patch(
        endpoints.clips.comments.update(clipId, commentId),
        {
          content,
        },
      )
      setEditingId(null)
      setEditingContent('')
      await fetchComments(1, false, false)
      toast.success('แก้ไขความคิดเห็นแล้ว')
    } catch (error) {
      console.error('update clip comment error', error)
      toast.error('แก้ไขความคิดเห็นล้มเหลว')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDelete = async (commentId: string) => {
    if (!window.confirm('ต้องการลบความคิดเห็นนี้หรือไม่?')) return

    setIsSubmitting(true)
    try {
      await axiosInstance.delete(
        endpoints.clips.comments.delete(clipId, commentId),
      )
      await fetchComments(1, false, false)
      toast.success('ลบความคิดเห็นแล้ว')
    } catch (error) {
      console.error('delete clip comment error', error)
      toast.error('ลบความคิดเห็นล้มเหลว')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Card className="border rounded-lg space-y-0">
      <CardHeader className="px-4 sm:px-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg font-semibold text-black">
            <MessageSquare className="h-5 w-5" />
            ความคิดเห็น ({total})
          </CardTitle>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={isLoading || isSubmitting}
            onClick={() => fetchComments(1)}
          >
            <RefreshCw className="h-4 w-4" />
            รีเฟรช
          </Button>
        </div>
      </CardHeader>
      <CardContent className="px-4 sm:px-6 pt-0 space-y-4">
        <div className="space-y-2">
          <Textarea
            value={newContent}
            onChange={(event) => setNewContent(event.target.value)}
            placeholder="เพิ่มความคิดเห็นในนามแอดมิน..."
            className="min-h-24 resize-none"
          />
          <Button
            type="button"
            disabled={isSubmitting || !newContent.trim()}
            onClick={handleCreate}
          >
            {isSubmitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Plus className="h-4 w-4" />
            )}
            เพิ่มความคิดเห็น
          </Button>
        </div>

        {isLoading ? (
          <div className="py-8 text-center text-sm text-muted-foreground">
            กำลังโหลดความคิดเห็น...
          </div>
        ) : comments.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">
            ยังไม่มีความคิดเห็น
          </div>
        ) : (
          <div className="space-y-3">
            {comments.map((comment) => {
              const displayName = getDisplayName(comment)
              const avatar = getAvatar(comment)
              const isEditing = editingId === comment.id

              return (
                <article
                  key={comment.id}
                  className="flex gap-3 rounded-lg border bg-white p-3"
                >
                  <Avatar className="h-9 w-9 shrink-0">
                    <AvatarImage src={avatar} alt={displayName} />
                    <AvatarFallback>{displayName.slice(0, 2)}</AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1 space-y-2">
                    <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-black">
                          {displayName}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {fDateTime(comment.createdAt)}
                        </p>
                      </div>
                      <div className="flex shrink-0 gap-1">
                        {isEditing ? (
                          <>
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              disabled={isSubmitting}
                              onClick={() => handleUpdate(comment.id)}
                            >
                              <Save className="h-4 w-4" />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              disabled={isSubmitting}
                              onClick={() => {
                                setEditingId(null)
                                setEditingContent('')
                              }}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              disabled={isSubmitting}
                              onClick={() => handleEdit(comment)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              disabled={isSubmitting}
                              onClick={() => handleDelete(comment.id)}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>

                    {isEditing ? (
                      <Textarea
                        value={editingContent}
                        onChange={(event) =>
                          setEditingContent(event.target.value)
                        }
                        className="min-h-24 resize-none"
                      />
                    ) : (
                      <p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-gray-700">
                        {comment.content}
                      </p>
                    )}
                  </div>
                </article>
              )
            })}

            {hasMore && (
              <Button
                type="button"
                variant="outline"
                className="w-full"
                disabled={isLoadingMore || isSubmitting}
                onClick={() => fetchComments(page + 1, true)}
              >
                {isLoadingMore ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  'โหลดความคิดเห็นเพิ่มเติม'
                )}
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
